import React, { useEffect, useMemo, useState } from 'react'
import useTimeOffDispatchers from "../../../redux/dispatchers/useTimeOffDispatchers"
import { useMoreTimeOffsLeftInPaginationSelector, useTimeOffsGetErrorMessageSelector, useTimeOffsGetStatusSelector, useTimeOffsSelector } from "../../../redux/selectors/useTimeOffSelectors"
import usePermissionsController from '../../../permissions'
import { FormDialog } from '../../../Components/Modals'
import { Box, ErrorAndSuccess, FlexBox } from '../../../Components/Boxes'
import DataTable, { StatusDot } from '../../../Components/DataTable'
import { Text } from '../../../Components/Text'
import { formateDateToReadableFormatWithTime } from '../../../utiles/dateformate'
import { useResponsive } from '../../../Components/Hooks'
import { Button, CheckBox } from '../../../Components/Input'
import Icon from '../../../Components/Icons'
import EmptyState from '../../../Components/NoRecordFound'
import { GridContainer, Grid } from '../../../Components/Grid'
import * as asyncStates from "../../../redux/constants/asyncStates";
import { TIME_OFF_STATUS, TIME_SHEET_STATUS } from '../../../constants'
import AddTimeOffDialog from './AddTimeOffDialog'

export default function TimeOff(props) {
  const isDesktop = useResponsive({ xs: false, sm: true });
  const { staffId } = props
  const timeOffs = useTimeOffsSelector()
  const getAllTimeOffStatus = useTimeOffsGetStatusSelector()
  const getAllTimeOffError = useTimeOffsGetErrorMessageSelector()
  const moreTimeOffItemsLeftInPagination = useMoreTimeOffsLeftInPaginationSelector()

  const [timeOffStatus, setTimeOffStatus] = useState([])

  const {
    allowApproveTimeOff,
  } = usePermissionsController()

  const {
    clearAllErrorMessages,
    clearAllLocalTimeOffs,
    clearAllSuccessMessages,
    clearFilters,
    updateTimeOffStatusFilter,
    getTimeOffs,
  } = useTimeOffDispatchers()

  const [open, setOpen] = useState(false);
  const [timeOffToEdit, setTimeOffToEdit] = useState(null);

  function HandleDialog() {
    setOpen(!open);
    setTimeOffToEdit(null);
  }

  const loadMore = () => {
    getTimeOffs({
      staffId: staffId,
      startAfter: timeOffs[timeOffs.length - 1].id,
    });
  }

  useEffect(() => {
    clearAllErrorMessages()
    clearAllSuccessMessages()
    clearAllLocalTimeOffs()
    clearFilters()
    updateTimeOffStatusFilter(timeOffStatus)
    getTimeOffs({
      staffId: staffId,
    })
  }, [timeOffStatus])

  const timeOffStatusFilterOptions = useMemo(() => {
    return [
      { value: TIME_OFF_STATUS.PENDING, label: "Pending" },
      { value: TIME_OFF_STATUS.ACCEPTED, label: "Accepted" },
      { value: TIME_OFF_STATUS.REJECTED, label: "Rejected" },
      { value: TIME_OFF_STATUS.CANCELLED, label: "Cancelled" },
    ]
  }, [])

  const statusColor = (status) => {
    switch (status) {
      case TIME_OFF_STATUS.PENDING:
        return "orange"
      case TIME_OFF_STATUS.ACCEPTED:
        return "green"
      case TIME_OFF_STATUS.REJECTED:
        return "red"
      case TIME_OFF_STATUS.CANCELLED:
        return "red"
      default:
        return "orange"
    }
  }

  const columns = [
    { key: "staff", header: "Staff", width: "20%", primary: true, render: (value) => <strong>{value?.staff?.name}</strong> },
    { key: "start", header: "Start", width: "17%", render: (value) => (value.creationDate ? formateDateToReadableFormatWithTime(new Date(value.startDate)) : "-") },
    { key: "end", header: "End", width: "17%", render: (value) => (value.creationDate ? formateDateToReadableFormatWithTime(new Date(value.endDate)) : "-") },
    { key: "reason", header: "Reason", width: "36%", render: (value) => value?.reason ?? "" },
    {
      key: "status",
      header: "Status",
      width: "10%",
      align: "center",
      render: (value) => {
        const c = statusColor(value.timeOffStatus)
        const label = timeOffStatusFilterOptions.find((o) => o.value === value.timeOffStatus)?.label
        return <StatusDot color={c} label={label} />
      },
    },
  ]

  const addButton = (
    <Button
      $onClick={() => {
        HandleDialog();
      }}
    >
      <Icon $icon="Add" $color="white"></Icon>
      <Text $color={"white"}>Add time off</Text>
    </Button>
  )

  return (
    <Box
      $width="100%"
      $backgroundColor="#fff"
    >
      <FlexBox $justifyContent={"flex-end"} $width="100%" $marginTop={2}>
        {addButton}
      </FlexBox>

      <GridContainer $spacing={3}>
        {timeOffStatusFilterOptions.map((value, index) => {
          return (
            <Grid key={index} xs={1}>
              <FlexBox $marginRight="3">
                <CheckBox
                  $checked={timeOffStatus.includes(value.value)}
                  $size={"medium"}
                  $onChange={(val) => {
                    const tempTimeOffStatus = new Set(timeOffStatus)
                    if (val.target.checked) {
                      tempTimeOffStatus.add(value.value)
                    } else {
                      tempTimeOffStatus.delete(value.value)
                    }
                    const timeOffStatusArray = []
                    tempTimeOffStatus.forEach((d) => {
                      timeOffStatusArray.push(d)
                    })
                    setTimeOffStatus(timeOffStatusArray)
                  }}
                />
                <Text $color={"text"} $fontSize={"body-sm"} $fontWeight={"600"}>
                  {value.label}
                </Text>
              </FlexBox>
            </Grid>
          )
        })}
      </GridContainer>

      <Box $width="100%" $marginTop={3}>
        <DataTable
          ariaLabel="Time off"
          columns={columns}
          rows={timeOffs}
          getRowKey={(value, index) => value.id ?? index}
          onRowClick={(value) => {
            HandleDialog();
            setTimeOffToEdit(value);
          }}
          loading={getAllTimeOffStatus === asyncStates.PENDING}
          skeletonRows={4}
          empty={
            getAllTimeOffStatus === asyncStates.SUCCESS && timeOffs.length < 1 ? (
              <EmptyState action={addButton} />
            ) : null
          }
        />
      </Box>

      {getAllTimeOffError && <FlexBox $marginTop={2} $width="100%">
        <ErrorAndSuccess $type="error">{getAllTimeOffError}</ErrorAndSuccess>
      </FlexBox>}

      {moreTimeOffItemsLeftInPagination && <FlexBox $marginTop="6" $justifyContent="center">
        <Button $onClick={() => {
          loadMore()
        }} >Load More</Button>
      </FlexBox>}

      {open && <FormDialog
        $open={open}
        $handleDialog={HandleDialog}
        $title={"Time Off"}
        $maxWidth="sm"
        $fullWidth
        $fullScreen={!isDesktop}
      >
        <AddTimeOffDialog
          $handleDialog={HandleDialog}
          $staffId={staffId}
          $timeOffToEdit={timeOffToEdit}
        />
      </FormDialog>}

    </Box>
  )
}
